
import React from 'react';
import { ShortContentCard } from './ShortContentCard';
import { ShortContentItem } from '@/hooks/useShortContent';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertCircle, FileText, Sparkles } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';

interface ShortContentGridProps {
  items: ShortContentItem[];
  isLoading?: boolean;
  onItemView?: (id: string) => void;
  compact?: boolean;
  className?: string;
  showActions?: boolean;
  columns?: 1 | 2 | 3 | 4;
  onRefresh?: () => void;
  error?: Error | null;
}

const LoadingSkeleton = ({ columns }: { columns: number }) => {
  // Enhanced responsive grid classes with better spacing
  const gridColumnsClass = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 sm:grid-cols-2',
    3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
  }[columns];

  return (
    <div className={`grid ${gridColumnsClass} gap-4 sm:gap-6 animate-fade-in`}>
      {Array(columns * 2).fill(0).map((_, i) => (
        <div key={i} className="flex flex-col space-y-3 animate-scale-in" style={{ animationDelay: `${i * 100}ms` }}>
          <Skeleton className="h-[180px] w-full rounded-md" />
          <div className="space-y-2 p-4">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-2/3" />
          </div>
        </div>
      ))}
    </div>
  );
};

const EmptyState = ({ onRefresh, error }: { onRefresh?: () => void; error?: Error | null }) => {
  if (error) {
    return (
      <Alert variant="destructive" className="max-w-md mx-auto animate-fade-in">
        <AlertCircle className="h-5 w-5" />
        <AlertDescription className="space-y-3">
          <div>
            <p className="font-medium">Failed to load content</p>
            <p className="text-sm opacity-90">{error.message}</p>
          </div>
          {onRefresh && (
            <Button variant="outline" size="sm" onClick={onRefresh} className="w-full">
              Try Again
            </Button>
          )}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="text-center py-16 animate-fade-in">
      <div className="flex flex-col items-center gap-4 max-w-md mx-auto">
        <div className="p-4 rounded-full bg-muted">
          <FileText className="h-8 w-8 text-muted-foreground" />
        </div>
        <div className="space-y-2">
          <h3 className="text-lg font-semibold">No content available</h3>
          <p className="text-muted-foreground text-sm">
            We're working on bringing you fresh travel insights. Check back soon!
          </p>
        </div>
        {onRefresh && (
          <Button variant="outline" onClick={onRefresh} className="mt-2">
            <Sparkles className="h-4 w-4 mr-2" />
            Refresh Content
          </Button>
        )}
      </div>
    </div>
  );
};

export function ShortContentGrid({
  items,
  isLoading = false,
  onItemView,
  compact = false,
  className = '',
  showActions = true,
  columns = 3,
  onRefresh,
  error
}: ShortContentGridProps) {
  // Enhanced responsive grid classes with better mobile spacing
  const gridColumnsClass = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 sm:grid-cols-2',
    3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
  }[columns];

  // Enhanced gap classes for better mobile spacing
  const gapClass = compact ? 'gap-3 sm:gap-4' : 'gap-4 sm:gap-6';

  console.log('ShortContentGrid rendering with:', {
    itemsLength: items?.length || 0, 
    isLoading,
    error: error?.message,
    compact,
    columns
  });

  if (isLoading) {
    return <LoadingSkeleton columns={columns} />;
  }

  // Show empty state if no items or error
  if (!items || !Array.isArray(items) || items.length === 0 || error) {
    return <EmptyState onRefresh={onRefresh} error={error} />;
  }

  return (
    <div className={`grid ${gridColumnsClass} ${gapClass} animate-fade-in ${className}`}>
      {items.map((item, index) => {
        if (!item || !item.id) {
          console.error('Invalid item found at index', index, ':', item);
          return null;
        }
        
        return (
          <div 
            key={item.id} 
            className="animate-scale-in" 
            style={{ animationDelay: `${index * 50}ms` }}
          >
            <ShortContentCard
              item={item}
              onView={onItemView}
              compact={compact}
              showActions={showActions}
            />
          </div>
        );
      }).filter(Boolean)}
    </div>
  );
}
